import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { apiClient } from '../../services';
import Layout from '../../components/layout/Layout';

// API 응답 타입 정의
interface ApiLectureInfo {
  id: number;
  title: string;
  caption: string;
  thumbnail_url: string;
  name: string;
  tags: string[];
  introduction: string;
}

interface ApiLecturerInfo {
  id: number;
  name: string;
  introduction: string;
  image_url: string;
}

interface ApiLectureItem {
  lecture_info: ApiLectureInfo;
  lecturer_info: ApiLecturerInfo;
}

interface ApiResponse {
  code: number;
  status: string;
  message: string;
  data: {
    content: ApiLectureItem[];
    totalPages: number;
    totalElements: number;
    pageable: {
      pageNumber: number;
      pageSize: number;
    };
  };
}

// 컴포넌트에서 사용할 강의 타입
interface Lecture {
  id: number;
  title: string;
  caption: string;
  name: string;
  tags: string[];
  introduction: string;
  extraCaption: string;
  image: string;
}

const ClassIndex: React.FC = () => {
  const navigate = useNavigate();
  const [bannerPage, setBannerPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  
  // 이미지 로딩 상태 관리
  const [imageLoadStates, setImageLoadStates] = useState<{[key: string]: boolean}>({});

  // API 데이터 상태 관리
  const [sectionData, setSectionData] = useState<{[key: number]: Lecture[]}>({
    1: [],
    2: [],
    3: []
  });
  
  // 전체 강의 데이터 상태 관리 (무한스크롤용)
  const [allLecturesData, setAllLecturesData] = useState<Lecture[]>([]);
  
  // 무한스크롤 관련 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PAGE_SIZE = 12;
  
  const [loading, setLoading] = useState<{[key: number]: boolean}>({
    1: true,
    2: true,
    3: true
  });
  
  const [allLecturesLoading, setAllLecturesLoading] = useState(true);
  
  const [error, setError] = useState<{[key: number]: string | null}>({
    1: null,
    2: null,
    3: null
  });

  const [allLecturesError, setAllLecturesError] = useState<string | null>(null);

  // 배너 드래그 상태 관리
  const [bannerDrag, setBannerDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    offset: 0
  });

  // API 호출 함수 - 첫 페이지(4개)만 가져오기
  const fetchSectionData = async (sectionId: number) => {
    try {
      setLoading(prev => ({...prev, [sectionId]: true}));
      setError(prev => ({...prev, [sectionId]: null}));
      
      // 첫 페이지만 가져오기 (4개 항목)
      console.log('요청:', `/api/lectures/info/list?page=0&size=4&sectionId=${sectionId}`);
      const { data: result } = await apiClient.get<ApiResponse>(
        `/api/lectures/info/list`,
        { params: { page: 0, size: 4, sectionId } }
      );
      console.log('응답 object:', result);
      
      const lectures: Lecture[] = result.data.content.map(item => ({
        id: item.lecture_info.id,
        title: item.lecture_info.title,
        caption: item.lecture_info.caption,
        name: item.lecture_info.name,
        tags: item.lecture_info.tags,
        introduction: item.lecture_info.introduction,
        extraCaption: `${item.lecturer_info.name} · ${item.lecturer_info.introduction}`, // lecturer 정보 조합
        image: item.lecture_info.thumbnail_url
      }));
      console.log({lectures});
      
      setSectionData(prev => ({...prev, [sectionId]: lectures}));
    } catch (err) {
      console.error('Section 데이터 로딩 실패:', err);
      setError(prev => ({...prev, [sectionId]: err instanceof Error ? err.message : '데이터를 불러오는데 실패했습니다.'}));
    } finally {
      setLoading(prev => ({...prev, [sectionId]: false}));
    }
  };

  // 전체 강의 데이터 호출 함수 (무한스크롤용)
  const fetchAllLecturesData = async (page: number = 0, isLoadMore: boolean = false) => {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setAllLecturesLoading(true);
        setAllLecturesError(null);
      }
      
      // 전체 강의 데이터 가져오기
      console.log('요청:', `/api/lectures/info/list?page=${page}&size=${PAGE_SIZE}`);
      const { data: result } = await apiClient.get<ApiResponse>(
        `/api/lectures/info/list`,
        { params: { page, size: PAGE_SIZE } }
      );
      console.log('응답 object:', result);
      
      const lectures: Lecture[] = result.data.content.map(item => ({
        id: item.lecture_info.id,
        title: item.lecture_info.title,
        caption: item.lecture_info.caption,
        name: item.lecture_info.name,
        tags: item.lecture_info.tags,
        introduction: item.lecture_info.introduction,
        extraCaption: `${item.lecturer_info.name} · ${item.lecturer_info.introduction}`, // lecturer 정보 조합
        image: item.lecture_info?.thumbnail_url
      }));
      
      if (isLoadMore) {
        // 더 로드하는 경우 기존 데이터에 추가
        setAllLecturesData(prevData => [...prevData, ...lectures]);
      } else {
        // 초기 로드인 경우 새 데이터로 설정
        setAllLecturesData(lectures);
      }
      
      // 더 로드할 데이터가 있는지 확인
      const totalPages = result.data.totalPages;
      setHasMoreData(page + 1 < totalPages);
      setCurrentPage(page);
      
    } catch (err) {
      console.error('전체 강의 데이터 로딩 실패:', err);
      if (!isLoadMore) {
        setAllLecturesError(err instanceof Error ? err.message : '데이터를 불러오는데 실패했습니다.');
      }
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setAllLecturesLoading(false);
      }
    }
  };



  // 컴포넌트 마운트 시 모든 섹션 데이터 로드
  useEffect(() => {
    fetchSectionData(1);
    fetchSectionData(2);
    fetchSectionData(3);
    fetchAllLecturesData(0, false); // 첫 페이지 로드
  }, []);

  // 무한스크롤 구현
  useEffect(() => {
    const handleScroll = () => {
      // 페이지 하단에서 200px 위에 도달하면 더 로드
      if (
        window.innerHeight + document.documentElement.scrollTop 
        >= document.documentElement.offsetHeight - 200
      ) {
        if (!isLoadingMore && hasMoreData) {
          fetchAllLecturesData(currentPage + 1, true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage, hasMoreData, isLoadingMore]);

  // 더 많은 강의 로드 핸들러 (수동 버튼용 - 제거 예정)
  const handleLoadMore = () => {
    if (!isLoadingMore && hasMoreData) {
      fetchAllLecturesData(currentPage + 1, true);
    }
  };

  // 카테고리 아이콘 데이터
  const categories = [
    { id: 1, title: '인공지능 기초', icon: '/images/category-icons/ai-basic.svg', categoryId: 'ai-basic' },
    { id: 2, title: '데이터 분석', icon: '/images/category-icons/data-analysis.svg', categoryId: 'data-analysis' },
    { id: 3, title: '의료 AI', icon: '/images/category-icons/medical-ai.svg', categoryId: 'medical-ai' },
    { id: 4, title: '실습 환경 · 코드', icon: '/images/category-icons/coding-environment.svg', categoryId: 'coding-environment' },
    { id: 5, title: '디지털 헬스케어', icon: '/images/category-icons/digital-healthcare.svg', categoryId: 'digital-healthcare' },
    { id: 6, title: '미래 의료인의 역할', icon: '/images/category-icons/future-doctor.svg', categoryId: 'future-doctor' },
  ];

  // 카테고리 클릭 핸들러
  const handleCategoryClick = (categoryId: string) => {
    navigate(`/class/category?category=${categoryId}`);
  };

  // 클래스 카드 클릭 핸들러
  const handleClassClick = (lectureId: number) => {
    navigate(`/class/lecture/${lectureId}`);
  };

  // 배너 데이터 (확장 가능)
  const bannerData = [
    // { id: 1, image: '/images/banner1.jpg', alt: '의료 교육 - 학생들이 병원에서 실습 중' },
    // { id: 2, image: '/images/banner2.jpg', alt: '의료 교육 - 청진기와 의학 교재' },
    // { id: 3, image: '/images/banner3.jpg', alt: '의료 교육 - 심장 해부학 모델' },
    
    { id: 1, image: '/images/clf.png', alt: '의료 교육 - 학생들이 병원에서 실습 중' },
    { id: 2, image: '/images/clf.png', alt: '의료 교육 - 청진기와 의학 교재' },
    { id: 3, image: '/images/clf.png', alt: '의료 교육 - 심장 해부학 모델' },
    
    // 필요시 배너 추가 가능
  ];

  const totalBanners = bannerData.length;

  // 무한 슬라이드를 위한 확장된 배너 데이터
  const extendedBannerData = [
    bannerData[bannerData.length - 1], // 마지막 배너를 앞에 추가
    ...bannerData,
    bannerData[0] // 첫 번째 배너를 뒤에 추가
  ];

  // 무한 슬라이드를 위한 transition 제어
  const [isTransitioning, setIsTransitioning] = useState(true);

  // 배너 페이지 변경 핸들러 (무한 슬라이드)
  const handleBannerPageChange = useCallback((direction: 'prev' | 'next') => {
    setIsTransitioning(true);
    
    if (direction === 'prev') {
      if (bannerPage === 1) {
        setBannerPage(bannerPage - 1); // 0으로 이동 (복사본)
      } else {
        setBannerPage(bannerPage - 1);
      }
    } else {
      if (bannerPage === totalBanners) {
        setBannerPage(bannerPage + 1); // totalBanners + 1로 이동 (복사본)
      } else {
        setBannerPage(bannerPage + 1);
      }
    }
  }, [bannerPage, totalBanners]);

  // 자동 슬라이드 효과 (15초마다)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!bannerDrag.isDragging) {
        handleBannerPageChange('next');
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [bannerDrag.isDragging, handleBannerPageChange]);

  // 무한 슬라이드 처리를 위한 useEffect
  useEffect(() => {
    if (bannerPage === 0) {
      // 첫 번째 복사본에서 실제 마지막으로 이동
      setTimeout(() => {
        setIsTransitioning(false);
        setBannerPage(totalBanners);
      }, 600);
    } else if (bannerPage === totalBanners + 1) {
      // 마지막 복사본에서 실제 첫 번째로 이동
      setTimeout(() => {
        setIsTransitioning(false);
        setBannerPage(1);
      }, 600);
    }
  }, [bannerPage, totalBanners]);

  // transition 복구
  useEffect(() => {
    if (!isTransitioning) {
      setTimeout(() => setIsTransitioning(true), 50);
    }
  }, [isTransitioning]);

  // 배너 닷 클릭 핸들러
  const handleBannerDotClick = (pageNumber: number) => {
    setIsTransitioning(true);
    setBannerPage(pageNumber);
  };

  // 검색 핸들러
  const handleSearchSubmit = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        alert(`${searchTerm} 검색`);
      }
    }
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  // 이미지 로드 핸들러
  const handleImageLoad = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: true
    }));
  };

  // 이미지 에러 핸들러
  const handleImageError = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: false
    }));
  };

  // 현재 실제 페이지 번호 계산
  const getCurrentPage = () => {
    if (bannerPage === 0) return totalBanners;
    if (bannerPage === totalBanners + 1) return 1;
    return bannerPage;
  };



  // 배너 드래그 핸들러
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    setBannerDrag({
      isDragging: true,
      startX: clientX,
      currentX: clientX,
      offset: 0
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    if (bannerDrag.isDragging) {
      const dragDistance = bannerDrag.currentX - bannerDrag.startX;
      const threshold = 100;
      
      if (dragDistance > threshold) {
        handleBannerPageChange('prev');
      } else if (dragDistance < -threshold) {
        handleBannerPageChange('next');
      }
      
      setBannerDrag({
        isDragging: false,
        startX: 0,
        currentX: 0,
        offset: 0
      });
    }
  }, [bannerDrag]);

  // 전역 마우스 이벤트 핸들러 (배너만)
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (bannerDrag.isDragging) {
        const offset = e.clientX - bannerDrag.startX;
        setBannerDrag(prev => ({
          ...prev,
          currentX: e.clientX,
          offset: offset
        }));
      }
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (bannerDrag.isDragging) {
        const offset = e.touches[0].clientX - bannerDrag.startX;
        setBannerDrag(prev => ({
          ...prev,
          currentX: e.touches[0].clientX,
          offset: offset
        }));
      }
    };

    const handleGlobalMouseUp = () => {
      if (bannerDrag.isDragging) {
        handleDragEnd();
      }
    };

    if (bannerDrag.isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove);
      document.addEventListener('touchend', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [bannerDrag, handleDragEnd]);

  return (
    <Layout>
      <Container>
        {/* 메인 콘텐츠 컨테이너 */}
        <MainContainer>
          {/* 메인 배너 슬라이드 */}
          <BannerSection>
            <BannerSlideWrapper 
              onMouseDown={(e) => handleDragStart(e)}
              onTouchStart={(e) => handleDragStart(e)}
              style={{ 
                transform: `translateX(calc(-${(bannerPage) * (100/(totalBanners + 2))}% + ${bannerDrag.isDragging ? bannerDrag.offset : 0}px))`,
                transition: bannerDrag.isDragging || !isTransitioning ? 'none' : 'transform 0.6s ease',
                width: `${(totalBanners + 2) * 100}%`
              }}
            >
              {extendedBannerData.map((banner, index) => (
                <BannerSlide key={`${banner.id}-${index}`} style={{ width: `${100/(totalBanners + 2)}%` }}>
                  <BannerContainer>
                    <BannerImageWrapper>
                      {!imageLoadStates[`banner-${banner.id}-${index}`] && (
                        <BannerImageSkeleton />
                      )}
                      <BannerImage 
                        src={banner.image} 
                        alt={banner.alt}
                        style={{ 
                          display: imageLoadStates[`banner-${banner.id}-${index}`] ? 'block' : 'none' 
                        }}
                        onLoad={() => handleImageLoad(`banner-${banner.id}-${index}`)}
                        onError={() => handleImageError(`banner-${banner.id}-${index}`)}
                      />
                    </BannerImageWrapper>
                  </BannerContainer>
                </BannerSlide>
              ))}
            </BannerSlideWrapper>
            
            {/* 페이지네이션 닷을 배너 섹션에 고정 */}
            <BannerPaginationContainer>
              <PaginationDots>
                {bannerData.map((_, dotIndex) => (
                  <Dot 
                    key={dotIndex}
                    active={dotIndex + 1 === getCurrentPage()}
                    onClick={() => handleBannerDotClick(dotIndex + 1)}
                  />
                ))}
              </PaginationDots>
            </BannerPaginationContainer>
          </BannerSection>

          {/* 카테고리 아이콘들과 검색바 */}
          <CategorySection>
            <CategoryIconGrid>
              {categories.map((category) => (
                <CategoryIconItem 
                  key={category.id}
                  onClick={() => handleCategoryClick(category.categoryId)}
                >
                  <CategoryIconBox>
                    <CategoryIcon>
                      {!imageLoadStates[`category-${category.id}`] && (
                        <CategoryIconSkeleton />
                      )}
                      <img 
                        src={category.icon} 
                        alt={category.title}
                        style={{ 
                          display: imageLoadStates[`category-${category.id}`] ? 'block' : 'none' 
                        }}
                        onLoad={() => handleImageLoad(`category-${category.id}`)}
                        onError={() => handleImageError(`category-${category.id}`)}
                      />
                    </CategoryIcon>
                  </CategoryIconBox>
                  <CategoryTitle>{category.title}</CategoryTitle>
                </CategoryIconItem>
              ))}
            </CategoryIconGrid>
            
            {/* 검색바 */}
            {/* <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path 
                      d="M14.375 14.375L18.125 18.125M16.25 9.375C16.25 13.1716 13.1716 16.25 9.375 16.25C5.57842 16.25 2.5 13.1716 2.5 9.375C2.5 5.57842 5.57842 2.5 9.375 2.5C13.1716 2.5 16.25 5.57842 16.25 9.375Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder="관심 주제, 강의 찾기"
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchSubmit}
                />
              </SearchBox>
            </SearchContainer> */}
          </CategorySection>

          {/* 첫 번째 클래스 섹션 */}
          <ClassSection>
            <SectionHeader>
              <SectionInfo>
                <SectionTitle>의료 AI 입문 – 개념부터 실습까지</SectionTitle>
                <SectionCaption>의료 AI를 처음 접하는 분들을 위해, 인공지능의 기본 개념부터 의료 데이터의 흐름, 그리고 실습 환경 구축까지 순차적으로 학습할 수 있는 입문 과정입니다.</SectionCaption>
              </SectionInfo>
            </SectionHeader>
            <ClassGrid>
              {loading[1] ? (
                // 로딩 중일 때 스켈레톤 UI 표시 (4개)
                Array.from({length: 4}).map((_, cardIndex) => (
                  <ClassCard key={`skeleton-${cardIndex}`}>
                    <ClassThumbnail>
                      <ClassImageSkeleton />
                    </ClassThumbnail>
                    <ClassInfo>
                      <TagSkeleton />
                      <ClassTitleSkeleton />
                      <ClassExtraCaptionSkeleton />
                    </ClassInfo>
                  </ClassCard>
                ))
              ) : error[1] ? (
                // 에러 상태일 때
                <div style={{ 
                  width: '100%', 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: 'rgba(55, 56, 60, 0.61)' 
                }}>
                  데이터를 불러오는데 실패했습니다: {error[1]}
                </div>
              ) : (
                // 정상 데이터 표시 (4개 고정)
                sectionData[1].map((classItem) => (
                  <ClassCard 
                    key={classItem.id}
                    onClick={() => handleClassClick(classItem.id)}
                  >
                    <ClassThumbnail>
                      {!imageLoadStates[`class-${classItem.id}`] && (
                        <ClassImageSkeleton />
                      )}
                      <ClassImage 
                        src={classItem.image} 
                        alt="클래스 썸네일"
                        style={{ 
                          display: imageLoadStates[`class-${classItem.id}`] ? 'block' : 'none' 
                        }}
                        onLoad={() => handleImageLoad(`class-${classItem.id}`)}
                        onError={() => handleImageError(`class-${classItem.id}`)}
                      />
                    </ClassThumbnail>
                    <ClassInfo>
                      {/* 태그 표시 */}
                      {classItem.tags && classItem.tags.length > 0 && (
                        <TagContainer>
                          {classItem.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </TagContainer>
                      )}
                      <ClassTitle>{classItem.title}</ClassTitle>
                      <ClassExtraCaption>{classItem.extraCaption}</ClassExtraCaption>
                    </ClassInfo>
                  </ClassCard>
                ))
              )}
            </ClassGrid>
          </ClassSection>

          {/* 두 번째 클래스 섹션 */}
          <ClassSection>
            <SectionHeader>
              <SectionInfo>
                <SectionTitle>의료 영상 AI – 적용과 실습으로 배우기</SectionTitle>
                <SectionCaption>AI가 실제 흉부와 유방 영상에서 어떻게 작동하는지를 임상 사례를 통해 이해하고, 파이썬을 활용한 실습으로 AI 모델을 직접 구현해보는 중급 과정입니다.</SectionCaption>
              </SectionInfo>
            </SectionHeader>
            <ClassGrid>
              {loading[2] ? (
                // 로딩 중일 때 스켈레톤 UI 표시 (4개)
                Array.from({length: 4}).map((_, cardIndex) => (
                  <ClassCard key={`skeleton-${cardIndex}`}>
                    <ClassThumbnail>
                      <ClassImageSkeleton />
                    </ClassThumbnail>
                    <ClassInfo>
                      <TagSkeleton />
                      <ClassTitleSkeleton />
                      <ClassExtraCaptionSkeleton />
                    </ClassInfo>
                  </ClassCard>
                ))
              ) : error[2] ? (
                // 에러 상태일 때
                <div style={{ 
                  width: '100%', 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: 'rgba(55, 56, 60, 0.61)' 
                }}>
                  데이터를 불러오는데 실패했습니다: {error[2]}
                </div>
              ) : (
                // 정상 데이터 표시 (4개 고정)
                sectionData[2].map((classItem) => (
                  <ClassCard 
                    key={`section2-${classItem.id}`}
                    onClick={() => handleClassClick(classItem.id)}
                  >
                    <ClassThumbnail>
                      {!imageLoadStates[`section2-class-${classItem.id}`] && (
                        <ClassImageSkeleton />
                      )}
                      <ClassImage 
                        src={classItem.image} 
                        alt="클래스 썸네일"
                        style={{ 
                          display: imageLoadStates[`section2-class-${classItem.id}`] ? 'block' : 'none' 
                        }}
                        onLoad={() => handleImageLoad(`section2-class-${classItem.id}`)}
                        onError={() => handleImageError(`section2-class-${classItem.id}`)}
                      />
                    </ClassThumbnail>
                    <ClassInfo>
                      {/* 태그 표시 */}
                      {classItem.tags && classItem.tags.length > 0 && (
                        <TagContainer>
                          {classItem.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </TagContainer>
                      )}
                      <ClassTitle>{classItem.title}</ClassTitle>
                      <ClassExtraCaption>{classItem.extraCaption}</ClassExtraCaption>
                    </ClassInfo>
                  </ClassCard>
                ))
              )}
            </ClassGrid>
          </ClassSection>

          {/* 세 번째 클래스 섹션 */}
          <ClassSection>
            <SectionHeader>
              <SectionInfo>
                <SectionTitle>의료 AI 고급 분석 – 딥러닝과 임상 적용의 접점</SectionTitle>
                <SectionCaption>인공신경망의 이론과 구조를 깊이 있게 학습하고, 실제 의료 데이터를 분석하며 임상 연구와 진료 의사결정에 AI를 적용하는 고급 수준의 실전 과정입니다.</SectionCaption>
              </SectionInfo>
            </SectionHeader>
            <ClassGrid>
              {loading[3] ? (
                // 로딩 중일 때 스켈레톤 UI 표시 (4개)
                Array.from({length: 4}).map((_, cardIndex) => (
                  <ClassCard key={`skeleton-${cardIndex}`}>
                    <ClassThumbnail>
                      <ClassImageSkeleton />
                    </ClassThumbnail>
                    <ClassInfo>
                      <TagSkeleton />
                      <ClassTitleSkeleton />
                      <ClassExtraCaptionSkeleton />
                    </ClassInfo>
                  </ClassCard>
                ))
              ) : error[3] ? (
                // 에러 상태일 때
                <div style={{ 
                  width: '100%', 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: 'rgba(55, 56, 60, 0.61)' 
                }}>
                  데이터를 불러오는데 실패했습니다: {error[3]}
                </div>
              ) : (
                // 정상 데이터 표시 (4개 고정)
                sectionData[3].map((classItem) => (
                  <ClassCard 
                    key={`section3-${classItem.id}`}
                    onClick={() => handleClassClick(classItem.id)}
                  >
                    <ClassThumbnail>
                      {!imageLoadStates[`section3-class-${classItem.id}`] && (
                        <ClassImageSkeleton />
                      )}
                      <ClassImage 
                        src={classItem.image} 
                        alt="클래스 썸네일"
                        style={{ 
                          display: imageLoadStates[`section3-class-${classItem.id}`] ? 'block' : 'none' 
                        }}
                        onLoad={() => handleImageLoad(`section3-class-${classItem.id}`)}
                        onError={() => handleImageError(`section3-class-${classItem.id}`)}
                      />
                    </ClassThumbnail>
                    <ClassInfo>
                      {/* 태그 표시 */}
                      {classItem.tags && classItem.tags.length > 0 && (
                        <TagContainer>
                          {classItem.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </TagContainer>
                      )}
                      <ClassTitle>{classItem.title}</ClassTitle>
                      <ClassExtraCaption>{classItem.extraCaption}</ClassExtraCaption>
                    </ClassInfo>
                  </ClassCard>
                ))
              )}
            </ClassGrid>
          </ClassSection>

          {/* 구분선 */}
          <Divider />

          {/* 전체 클래스 그리드 섹션 */}
          <AllClassesSection>
            <AllClassesGrid>
              {allLecturesLoading ? (
                // 로딩 중일 때 스켈레톤 UI 표시 (12개)
                Array.from({length: 12}).map((_, index) => (
                  <AllClassCard key={`skeleton-${index}`}>
                    <AllClassThumbnail>
                      <AllClassImageSkeleton />
                    </AllClassThumbnail>
                    <AllClassInfo>
                      <div style={{
                        alignSelf: 'stretch',
                        overflow: 'hidden',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: '6px',
                        display: 'inline-flex'
                      }}>
                        <AllClassBadgeSkeleton />
                      </div>
                      <div style={{
                        alignSelf: 'stretch',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: '4px',
                        display: 'flex'
                      }}>
                        <AllClassTitleSkeleton />
                        <AllClassCaptionSkeleton />
                      </div>
                    </AllClassInfo>
                  </AllClassCard>
                ))
              ) : allLecturesError ? (
                // 에러 상태일 때
                <div style={{ 
                  width: '100%', 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: 'rgba(55, 56, 60, 0.61)',
                  gridColumn: '1 / -1'
                }}>
                  데이터를 불러오는데 실패했습니다: {allLecturesError}
                </div>
              ) : (
                // 실제 데이터 표시
                allLecturesData.map((lecture) => (
                  <AllClassCard 
                    key={`all-lecture-${lecture.id}`}
                    onClick={() => handleClassClick(lecture.id)}
                  >
                    <AllClassThumbnail>
                      {!imageLoadStates[`all-lecture-${lecture.id}`] && (
                        <AllClassImageSkeleton />
                      )}
                      <AllClassImage 
                        src={lecture.image} 
                        alt={lecture.title}
                        style={{ 
                          display: imageLoadStates[`all-lecture-${lecture.id}`] ? 'block' : 'none' 
                        }}
                        onLoad={() => handleImageLoad(`all-lecture-${lecture.id}`)}
                        onError={() => handleImageError(`all-lecture-${lecture.id}`)}
                      />
                    </AllClassThumbnail>
                    <AllClassInfo>
                      <div style={{
                        alignSelf: 'stretch',
                        overflow: 'hidden',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: '6px',
                        display: 'inline-flex'
                      }}>
                        {/* 태그들 표시 */}
                        {lecture.tags && lecture.tags.length > 0 ? (
                          lecture.tags.map((tag, index) => (
                            <div key={index} style={{
                              height: '20px',
                              paddingLeft: '6px',
                              paddingRight: '6px',
                              background: 'rgba(68, 129, 129, 0.08)',
                              borderRadius: '6px',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: '4px',
                              display: 'flex'
                            }}>
                              <div style={{
                                justifyContent: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                color: 'var(--Primary-Normal, #448181)',
                                fontSize: '11px',
                                fontFamily: 'Pretendard JP',
                                fontWeight: '500',
                                lineHeight: '1',
                                letterSpacing: '0.34px',
                                wordWrap: 'break-word'
                              }}>{tag}</div>
                            </div>
                          ))
                        ) : (
                          <div style={{
                            height: '20px',
                            paddingLeft: '6px',
                            paddingRight: '6px',
                            background: 'rgba(68, 129, 129, 0.08)',
                            borderRadius: '6px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '4px',
                            display: 'flex'
                          }}>
                            <div style={{
                              justifyContent: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              color: 'var(--Primary-Normal, #448181)',
                              fontSize: '11px',
                              fontFamily: 'Pretendard JP',
                              fontWeight: '500',
                              lineHeight: '1',
                              letterSpacing: '0.34px',
                              wordWrap: 'break-word'
                            }}>강의</div>
                          </div>
                        )}
                      </div>
                      <div style={{
                        alignSelf: 'stretch',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: '4px',
                        display: 'flex'
                      }}>
                        <div style={{
                          alignSelf: 'stretch',
                          color: '#171719',
                          fontSize: '16px',
                          fontFamily: 'Pretendard JP',
                          fontWeight: '600',
                          lineHeight: '24px',
                          letterSpacing: '0.09px',
                          wordWrap: 'break-word'
                        }}>{lecture.title}</div>
                        <div style={{
                          alignSelf: 'stretch',
                          color: 'rgba(55, 56, 60, 0.61)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard JP',
                          fontWeight: '500',
                          lineHeight: '18px',
                          letterSpacing: '0.25px',
                          wordWrap: 'break-word'
                        }}>{`${lecture.extraCaption}`}</div>
                      </div>
                    </AllClassInfo>
                  </AllClassCard>
                ))
              )}
            </AllClassesGrid>
            
            {/* 무한스크롤 로딩 인디케이터 */}
            {!allLecturesLoading && (
              <LoadMoreSection>
                {isLoadingMore && (
                  <LoadMoreSkeleton>
                    <div>더 많은 강의를 불러오는 중...</div>
                  </LoadMoreSkeleton>
                )}
                {!hasMoreData && allLecturesData.length > 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    color: 'rgba(55, 56, 60, 0.61)', 
                    fontSize: '16px',
                    padding: '40px 0'
                  }}>
                    모든 강의를 확인했습니다
                  </div>
                )}
              </LoadMoreSection>
            )}
          </AllClassesSection>
        </MainContainer>
      </Container>
    </Layout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
`;

/* 메인 컨테이너 */
const MainContainer = styled.div`
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 24px ${({ theme }) => theme.layout.containerPadding} 128px;
  display: flex;
  flex-direction: column;
  gap: 48px; /* gap 다시 추가 */
  
  @media (max-width: 1024px) {
    padding: 24px ${({ theme }) => theme.layout.containerPaddingTablet} 128px;
  }
  
  @media (max-width: 768px) {
    padding: 24px ${({ theme }) => theme.layout.containerPaddingMobile} 100px;
    gap: 48px;
  }
`;

/* 배너 섹션 */
const BannerSection = styled.section`
  position: relative;
  width: 100%;
  margin-bottom: 0;
  overflow: hidden;
  border-radius: 12px;
  
  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const BannerSlideWrapper = styled.div`
  display: flex;
  transition: transform 0.6s ease;
  will-change: transform;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  border-radius: 12px;
  
  &:active {
    cursor: grabbing;
  }
`;

const BannerSlide = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 0;
`;

const BannerContainer = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  aspect-ratio: 3/1; /* 1200:400 = 3:1 비율 */
  min-height: 200px; /* 최소 높이 보장 */
  
  @media (max-width: 1024px) {
    min-height: 150px;
  }
  
  @media (max-width: 768px) {
    min-height: 120px;
  }
`;

const BannerImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  left: 0px;
  top: 0px;
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
`;

const BannerPaginationContainer = styled.div`
  position: absolute;
  right: 20px;
  bottom: 20px;
  pointer-events: auto;
  z-index: 10;
  
  @media (max-width: 768px) {
    right: 16px;
    bottom: 16px;
  }
`;

const PaginationDots = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`;

const Dot = styled.div<{ active?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 1000px;
  background: white;
  outline: 1px solid rgba(112, 115, 124, 0.16);
  opacity: ${props => props.active ? 1 : 0.52};
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: ${props => props.active ? 1 : 0.8};
  }
`;

const CategorySection = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 32px;
  display: flex;
  
  @media (max-width: 768px) {
    gap: 24px;
  }
`;

const CategoryIconGrid = styled.div`
  height: 92px;
  justify-content: center;
  align-items: flex-end;
  gap: 12px;
  display: inline-flex;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    height: auto;
    gap: 8px;
  }
`;

const CategoryIconItem = styled.div`
  width: 116px;
  height: 92px;
  max-width: 116px;
  overflow: hidden;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  display: inline-flex;
  cursor: pointer;
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const CategoryIconBox = styled.div`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const CategoryIcon = styled.div`
  width: 64px;
  height: 64px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  min-width: 64px; /* 최소 크기 보장 */
  min-height: 64px;
  
  ${CategoryIconItem}:hover & {
    transform: translateY(-4px);
  }
  
  img {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: contain;
  }
  
  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }
`;

const CategoryTitle = styled.div`
  align-self: stretch;
  text-align: center;
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

// const SearchContainer = styled.div`
//   width: 100%;
//   max-width: 700px;
//   flex-direction: column;
//   justify-content: flex-start;
//   align-items: flex-start;
//   gap: 10px;
//   display: flex;
// `;

// const SearchBox = styled.div`
//   align-self: stretch;
//   height: 64px;
//   padding: 12px;
//   box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
//   overflow: hidden;
//   border-radius: 12px;
//   outline: 1px solid rgba(112, 115, 124, 0.22);
//   outline-offset: -1px;
//   justify-content: flex-start;
//   align-items: center;
//   gap: 8px;
//   display: inline-flex;
// `;

// const SearchIcon = styled.div`
//   height: 20px;
//   padding-left: 2px;
//   padding-right: 2px;
//   justify-content: center;
//   align-items: center;
//   display: flex;
// `;

// const SearchInput = styled.input`
//   flex: 1 1 0;
//   border: none;
//   outline: none;
//   background: transparent;
//   color: var(--Label-Normal, #171719);
//   font-size: 16px;
//   font-family: 'Pretendard JP', sans-serif;
//   font-weight: 400;
//   line-height: 24px;
//   letter-spacing: 0.09px;
  
//   &::placeholder {
//     color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
//   }
// `;

/* 섹션 공통 스타일 */
const ClassSection = styled.section`
  align-self: stretch;
  overflow: hidden;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;
  display: flex;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 12px;
  display: inline-flex;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const SectionInfo = styled.div`
  flex: 1 1 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  display: inline-flex;
`;

const SectionTitle = styled.div`
  align-self: stretch;
  color: var(--Label-Strong, black);
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SectionCaption = styled.div`
  align-self: stretch;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 26.01px;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const PaginationControls = styled.div`
  height: 38px;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  display: flex;
  
  @media (max-width: 768px) {
    align-self: flex-end;
  }
`;

const PaginationContainer = styled.div`
  position: relative;
  border-radius: 10px;
  outline: 1px solid rgba(112, 115, 124, 0.16);
  outline-offset: -1px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const PaginationContent = styled.div`
  overflow: hidden;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const PaginationBtn = styled.button<{ disabled?: boolean }>`
  padding: 7px;
  justify-content: center;
  align-items: center;
  display: flex;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  
  &:disabled {
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(23, 23, 25, 0.04);
      border-radius: 6px;
    }
  }
  
  &:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  
  &:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const PaginationIcon = styled.div<{ isRight?: boolean; isActive?: boolean }>`
  height: 18px;
  width: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

/* 슬라이드 애니메이션 컴포넌트 */
const SlideContainer = styled.div`
  width: 100%;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  
  &:active {
    cursor: grabbing;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  width: 300%; /* 3페이지 * 100% */
  transition: transform 0.6s ease;
  will-change: transform;
`;

const SlidePage = styled.div`
  width: calc(100% / 3); /* 전체 너비의 1/3 */
  flex-shrink: 0;
`;

const ClassGrid = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
  display: inline-flex;
  
  @media (max-width: 1024px) {
    gap: 16px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const ClassCard = styled.div`
  flex: 1 1 0;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  display: inline-flex;
  cursor: pointer;
  transition: none; /* 호버 효과 제거 */
`;

const ClassThumbnail = styled.div`
  align-self: stretch;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  border: 1px solid rgba(112, 115, 124, 0.08);
  aspect-ratio: 3/2;
  min-height: 188px; /* 최소 높이 보장 */
`;

const ClassImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  transition: transform 0.3s ease;
  
  ${ClassCard}:hover & {
    transform: scale(1.05);
  }
`;

const ClassInfo = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  display: flex;
`;

const ClassTitle = styled.div`
  align-self: stretch;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

const ClassCaption = styled.div`
  align-self: stretch;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
`;

const ClassExtraCaption = styled.div`
  align-self: stretch;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
`;

// 태그 컨테이너와 태그 스타일 추가
const TagContainer = styled.div`
  align-self: stretch;
  overflow: hidden;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
  display: inline-flex;
  margin-bottom: 4px;
`;

const Tag = styled.div`
  height: 20px;
  padding-left: 6px;
  padding-right: 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: flex;
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
  word-wrap: break-word;
`;

// 스켈레톤 UI 컴포넌트
const ClassImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ClassTitleSkeleton = styled.div`
  width: 80%;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ClassCaptionSkeleton = styled.div`
  width: 90%;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-top: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ClassExtraCaptionSkeleton = styled.div`
  width: 60%;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-top: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const TagSkeleton = styled.div`
  width: 50px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  margin-bottom: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const BannerImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const CategoryIconSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 구분선 컴포넌트
const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

// 전체 클래스 섹션
const AllClassesSection = styled.section`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  display: flex;
  /* margin-top: 48px; */
`;

const AllClassesGrid = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: flex-start;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-column-gap: 24px;
  grid-row-gap: 48px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 16px;
    grid-row-gap: 48px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 16px;
    grid-row-gap: 48px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(1, 1fr);
    grid-column-gap: 16px;
    grid-row-gap: 48px;
  }
`;

const AllClassCard = styled.div`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  display: inline-flex;
  cursor: pointer;
`;

const AllClassThumbnail = styled.div`
  align-self: stretch;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  aspect-ratio: 3/2;
`;

const AllClassImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  transition: transform 0.3s ease;
  
  ${AllClassCard}:hover & {
    transform: scale(1.05);
  }
`;

const AllClassInfo = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  display: flex;
`;

// 전체 클래스 그리드 스켈레톤 컴포넌트들
const AllClassImageSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  aspect-ratio: 3/2;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const AllClassBadgeSkeleton = styled.div`
  width: 40px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const AllClassTitleSkeleton = styled.div`
  width: 85%;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const AllClassCaptionSkeleton = styled.div`
  width: 95%;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

/* 더보기 버튼 관련 스타일 */
const LoadMoreSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 48px;
  
  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const LoadMoreButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 32px;
  background: white;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(68, 129, 129, 0.04);
    border-color: rgba(68, 129, 129, 0.3);
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const LoadMoreText = styled.span`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
`;

const LoadMoreIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #171719;
`;

const LoadMoreSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 32px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

export default ClassIndex;